import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import httpClient from '../../api/httpClient';

export type TCutOffTimeData = {
  ccyId: string;
  currency: string;
  time: string;
};

export type TCutOffTime = {
  data: TCutOffTimeData[];
};

export const fetchCutOffTime = createAsyncThunk<TCutOffTime, { paramsId: string }>(
  'cutOffTime/fetch',
  async ({ paramsId }) => {
    try {
      const response = await httpClient
        .get(`/api/v1/bank-connections/${paramsId}/cutoff-time`)
        .then((response) => {
          return response.data;
        });
      return await response;
    } catch (e) {
      return console.log('cutOffTime/fetch error' + e);
    }
  }
);

export const addCutOffTime = createAsyncThunk<
  { ccyId: string },
  { currency: string; time: string; paramsId: string }
>('cutOffTime/post', async (props, thinkAPI) => {
  try {
    const response = await httpClient
      .post('/api/v1/bank-connections/cutoff-time', props, {
        signal: thinkAPI.signal,
      })
      .then((response) => {
        return response.data;
      });
    return await response;
  } catch (e) {
    return thinkAPI.rejectWithValue(true);
  }
});

export const editCutOffTime = createAsyncThunk<
  { ccyId: string },
  { ccyId: string; time: string; }
>('cutOffTime/edit', async (props, thinkAPI) => {
  try {
    const response = await httpClient
      .patch('/api/v1/bank-connections/cutoff-time', props, {
        signal: thinkAPI.signal,
      })
      .then((response) => {
        return response.data;
      });
    return await response;
  } catch (e) {
    return thinkAPI.rejectWithValue(true);
  }
});

export const deleteCutOffTime = createAsyncThunk<
  { ccyId: string },
  { ccyId: string }
>('cutOffTime/delete', async ({ ccyId }, thinkAPI) => {
  try {
    const response = await httpClient
      .delete(`/api/v1/bank-connections/cutoff-time/${ccyId}`, {
        signal: thinkAPI.signal,
      })
      .then((response) => {
        return response.data;
      });
    return await response;
  } catch (e) {
    return thinkAPI.rejectWithValue(true);
  }
});

interface CutOffTimeState {
  cutOffTime: TCutOffTime['data'] | null;
  ccyId: string | null;
  loading: boolean;
  error: boolean;
}

const initialState: CutOffTimeState = {
  cutOffTime: null,
  ccyId: null,
  loading: true,
  error: false,
};

export const cutOffTimeSlice = createSlice({
  name: 'cutOffTime',
  initialState,
  reducers: {
    setEditCcyId: (state, action) => {
      state.ccyId = action.payload;
    },
    setIsLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCutOffTime.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCutOffTime.fulfilled, (state, action) => {
      state.cutOffTime = action.payload.data;
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchCutOffTime.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(addCutOffTime.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(addCutOffTime.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(addCutOffTime.rejected, (state) => {
      state.error = true;
    });
    builder.addCase(editCutOffTime.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(editCutOffTime.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(editCutOffTime.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    
    builder.addCase(deleteCutOffTime.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteCutOffTime.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(deleteCutOffTime.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export default cutOffTimeSlice.reducer;
